import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LoggingService } from '../../shared/services/logging.service';
import { SnackBarService } from '../../shared/services/snackbar.service';
import { LabelService } from '../../shared/services/label.service';
import { environment } from 'src/environments/environment';
import { SKIP_ERROR_TOAST } from './error-toast.context';

// Auth/session-check, login and logout requests intentionally fail as part
// of normal flow (e.g. "whoami" returning 401 before any session exists, or
// a best-effort logout notification) — NavifyService/OAuth2Service already
// handle these silently by design, so the generic toast must not fire here.
const SILENT_TOAST_URL_FRAGMENTS: string[] = [
  environment.apiProfile,
  environment.apiLogout,
  environment.oauth2Config.startUrl,
  environment.oauth2Config.exchangeUrl,
  environment.oauth2Config.refreshUrl,
  environment.oauth2Config.logoutUrl
];

/**
 * Cross-cutting HTTP error handling: logs every failed HTTP call once (via
 * LoggingService) and shows exactly one user-facing toast (via
 * SnackBarService), mapped from the response status code to the
 * `i18n.ERROR.*` labels.
 *
 * This interceptor is the SINGLE owner of the generic "your request failed"
 * toast. Components must NOT also show a generic technical-error toast in
 * their `.subscribe({ error })` callback for HTTP failures — doing so would
 * show the user two toasts for the same failure. Component-level error
 * callbacks should be reserved for presentation-only concerns (inline error
 * state, disabling a button, keeping a form editable) or business-specific
 * messages that add information beyond "the request failed".
 *
 * Login/logout/session-check requests (see SILENT_TOAST_URL_FRAGMENTS) are
 * still logged but never toasted — a 401 on first hit (not logged in yet) or
 * a failed best-effort logout notification is expected, not a user-facing
 * error.
 *
 * Individual requests can also opt out via `SKIP_ERROR_TOAST` (see
 * `error-toast.context.ts`) for expected/already-handled business cases (e.g.
 * "no schema configured yet", "dataset not created yet") — still logged.
 *
 * This interceptor NEVER swallows an error — it always re-throws
 * (`throwError(() => error)`) so services/components still receive the
 * error and keep full control over their own component-level UX.
 *
 * Registered AFTER AuthInterceptor in CoreModule so auth headers are
 * already attached before this wraps the response.
 *
 * Deliberately does NOT retry requests in this phase — retrying would add
 * extra network calls that were not part of existing behavior/tests. Retry
 * for idempotent GETs can be introduced later as its own reviewable change.
 */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

  constructor(
    private readonly loggingService: LoggingService,
    private readonly snackBarService: SnackBarService,
    private readonly labelService: LabelService
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        this.loggingService.logError({
          apiUrl: request.urlWithParams,
          feature: 'HttpErrorInterceptor',
          errorMessage: error.message,
          statusCode: error.status,
          category: 'HttpError'
        });
        if (!this.isSilentToastRequest(request.url) && !request.context.get(SKIP_ERROR_TOAST)) {
          // this.showErrorToast(error.status);
        }
        // Always re-throw — callers still receive the error and decide
        // component-level UX; the interceptor never swallows the error.
        return throwError(() => error);
      })
    );
  }

  private isSilentToastRequest(url: string): boolean {
    return SILENT_TOAST_URL_FRAGMENTS.some((fragment) => !!fragment && url.includes(fragment));
  }

  private showErrorToast(status: number): void {
    const errorLabels = this.labelService.labels?.ERROR;
    // Labels may not be loaded yet (e.g. failure during app init) — skip the
    // toast rather than show a raw undefined message.
    if (!errorLabels) {
      return;
    }
    // this.snackBarService.displaySnackBar(this.messageForStatus(status, errorLabels), 'alert');
  }

  private messageForStatus(status: number, errorLabels: Record<string, string>): string {
    switch (status) {
      case 0:
        return errorLabels['NETWORK'];
      case 400:
        return errorLabels['BAD_REQUEST'];
      case 401:
        return errorLabels['SESSION_EXPIRED'];
      case 403:
        return errorLabels['FORBIDDEN'];
      case 404:
        return errorLabels['NOT_FOUND'];
      case 408:
      case 504:
        return errorLabels['TIMEOUT'];
      case 429:
        return errorLabels['TOO_MANY_REQUESTS'];
      default:
        return status >= 500 ? errorLabels['SERVER'] : errorLabels['UNEXPECTED'];
    }
  }
}

